/**
 * LocationSearch — search button injected into the Mapbox navigation control group.
 * Renders as the top button in the bottom-right control bar.
 * When clicked, a search panel expands above the controls.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMapStore } from '../../stores';
import { MAPBOX_TOKEN } from '../../services/mapboxToken';
import './LocationSearch.css';

interface GeoResult {
  id: string;
  place_name: string;
  center: [number, number];
  bbox?: [number, number, number, number];
  place_type: string[];
}

export default function LocationSearch() {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeoResult[]>([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [btnPortal, setBtnPortal] = useState<HTMLElement | null>(null);
  const [panelPortal, setPanelPortal] = useState<HTMLElement | null>(null);

  const flyTo = useMapStore((s) => s.flyTo);
  const mapInstance = useMapStore((s) => s.map);

  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Create a wrapper element prepended as first child of the Mapbox ctrl-group
  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getContainer();
    const group = container.querySelector('.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-group');
    const bottomRight = container.querySelector('.mapboxgl-ctrl-bottom-right');
    if (!(group instanceof HTMLElement) || !(bottomRight instanceof HTMLElement)) return;

    // Create a div to hold our button, prepend it to the ctrl-group
    const wrapper = document.createElement('div');
    wrapper.className = 'loc-search__btn-wrapper';
    group.prepend(wrapper);
    setBtnPortal(wrapper);
    setPanelPortal(bottomRight);

    return () => { wrapper.remove(); };
  }, [mapInstance]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (btnPortal?.contains(e.target as Node)) return;
      if (expanded) collapse();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded, btnPortal]);

  const collapse = () => {
    setExpanded(false);
    setQuery('');
    setResults([]);
    setShowResults(false);
    setActiveIdx(-1);
  };

  const toggleExpand = () => {
    if (expanded) {
      collapse();
    } else {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const encoded = encodeURIComponent(q);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&limit=5&language=he,en&types=country,region,place,locality,neighborhood,address,poi`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('Geocoding failed');
      const data = await res.json();
      const features: GeoResult[] = (data.features ?? []).map((f: any) => ({
        id: f.id,
        place_name: f.place_name,
        center: f.center as [number, number],
        bbox: f.bbox,
        place_type: f.place_type,
      }));
      setResults(features);
      setActiveIdx(-1);
      setShowResults(features.length > 0);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setResults([]);
        setShowResults(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (value: string) => {
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
  };

  const selectResult = (result: GeoResult) => {
    if (result.bbox && mapInstance) {
      const [w, s, e, n] = result.bbox;
      mapInstance.fitBounds([w, s, e, n], { padding: 40, duration: 1500 });
    } else {
      const type = result.place_type[0];
      let zoom = 14;
      if (type === 'country') zoom = 5;
      else if (type === 'region') zoom = 8;
      else if (type === 'place') zoom = 12;
      else if (type === 'locality' || type === 'neighborhood') zoom = 14;
      else if (type === 'address' || type === 'poi') zoom = 16;
      flyTo(result.center, zoom);
    }
    collapse();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      collapse();
      return;
    }
    if (!showResults || results.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIdx((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIdx((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIdx >= 0) selectResult(results[activeIdx]);
        break;
    }
  };

  if (!btnPortal || !panelPortal) return null;

  // Button portaled into the wrapper (prepended first child of ctrl-group)
  const searchButton = createPortal(
    <button
      className={`loc-search__btn${expanded ? ' loc-search__btn--active' : ''}`}
      onClick={toggleExpand}
      title="Search location"
      type="button"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </button>,
    btnPortal,
  );

  // Expanded panel rendered inside .mapboxgl-ctrl-bottom-right
  const searchPanel = expanded ? createPortal(
    <div className="loc-search__panel" ref={panelRef}>
      <div className="loc-search__input-row">
        <svg className="loc-search__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="loc-search__input"
          placeholder="Search location..."
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setShowResults(true); }}
        />
        {loading && <span className="loc-search__spinner" />}
      </div>

      {showResults && results.length > 0 && (
        <ul className="loc-search__results">
          {results.map((r, i) => (
            <li
              key={r.id}
              className={`loc-search__result${i === activeIdx ? ' loc-search__result--active' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); selectResult(r); }}
            >
              <svg className="loc-search__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className="loc-search__result-text">{r.place_name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    panelPortal,
  ) : null;

  return (
    <>
      {searchButton}
      {searchPanel}
    </>
  );
}
